import { loginRequest, apiRequest } from "@/authConfig";

/**
 * Authenticated API call utility using MSAL tokens
 * @param {string} endpoint - API endpoint URL
 * @param {Object} options - Fetch options (method, body, etc.)
 * @param {Object} msalInstance - MSAL instance from useMsal hook
 * @param {Array} accounts - Accounts array from useMsal hook
 * @returns {Promise} - API response
 */
export async function callApi(endpoint, options = {}, msalInstance, accounts) {
  if (!msalInstance || !accounts || accounts.length === 0) {
    throw new Error('User not authenticated - please login first');
  }

  try {
    // Get access token for API calls using API-specific scopes 
    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...apiRequest,  // Use API scopes instead of login scopes
      account: accounts[0],
    });

    if (import.meta.env.DEV) {
      console.log(`Making authenticated API call to: ${endpoint}`);
    }

    const { responseType, ...fetchOptions } = options;

    // Merge headers with auth token
    // Don't set Content-Type for FormData - browser will set it with boundary
    const headers = {
      'Authorization': `Bearer ${tokenResponse.accessToken}`,
      ...fetchOptions.headers
    };
    
    // Only add Content-Type for non-FormData requests
    if (!(fetchOptions.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Make the API call
    const response = await fetch(endpoint, {
      ...fetchOptions,
      headers
    });

    // Handle non-200 responses
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API call failed: ${response.status} - ${errorText}`);
    }

    if (responseType === 'blob') {
      return await response.blob();
    }

    // Check if response has content
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      return await response.text();
    }

  } catch (error) {
    console.error('Authenticated API call failed:', error);
    
    // If token acquisition fails, user needs to login again
    if (error.name === 'InteractionRequiredAuthError') {
      if (import.meta.env.DEV) {
        console.log('Token expired - please refresh the page to re-authenticate');
      }
      // Don't redirect here - let the component handle it or user can refresh
      throw new Error('Authentication required - please refresh the page');
    }
    
    throw error;
  }
}

/**
 * Enhanced React Hook for secure API calls with MSAL
 * Usage: const { callAuthenticatedApi, isLoading, error, secureHelpers } = useAuthenticatedApi();
 */
import { useState } from 'react';
import { useMsal } from '@azure/msal-react';

export function useAuthenticatedApi() {
  const { instance, accounts } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const callAuthenticatedApi = async (endpoint, options = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await callApi(endpoint, options, instance, accounts);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Secure helper methods that use the current MSAL context
  const secureHelpers = {
    // Basic HTTP methods with security features
    get: (endpoint, options = {}) => 
      apiHelpers.get(endpoint, instance, accounts, options),
    
    post: (endpoint, data, options = {}) => 
      apiHelpers.post(endpoint, data, instance, accounts, options),
    
    put: (endpoint, data, options = {}) => 
      apiHelpers.put(endpoint, data, instance, accounts, options),

    patch: (endpoint, data, options = {}) => 
      apiHelpers.patch(endpoint, data, instance, accounts, options),
    
    delete: (endpoint, options = {}) => 
      apiHelpers.delete(endpoint, instance, accounts, options),
    
    // Admin-specific methods
    admin: {
      getAllUsers: () => adminApiHelpers.getAllUsers(instance, accounts),
      createUser: (userData) => adminApiHelpers.createUser(userData, instance, accounts),
      updateUser: (userId, userData) => adminApiHelpers.updateUser(userId, userData, instance, accounts),
      deleteUser: (userId) => adminApiHelpers.deleteUser(userId, instance, accounts),
      getSystemSettings: () => adminApiHelpers.getSystemSettings(instance, accounts),
      updateSystemSettings: (settings) => adminApiHelpers.updateSystemSettings(settings, instance, accounts),
      getAuditLogs: (filters) => adminApiHelpers.getAuditLogs(instance, accounts, filters),
      getAllSurveys: () => adminApiHelpers.getAllSurveys(instance, accounts),
      closeSurvey: (surveyId) => adminApiHelpers.closeSurvey(surveyId, instance, accounts), 
      deleteSurvey: (surveyId) => adminApiHelpers.deleteSurvey(surveyId, instance, accounts),
      getSurveyInstanceGroups: (tags, includeClosed) => adminApiHelpers.getSurveyInstanceGroups(tags || [], includeClosed || false, instance, accounts),
      getSurveyInstancesByGroup: (groupKey) => adminApiHelpers.getSurveyInstancesByGroup(groupKey, instance, accounts),
      toggleSurveyInstance: (instanceId, isOpen) => adminApiHelpers.toggleSurveyInstance(instanceId, isOpen, instance, accounts),
      closeSurveyInstancesByGroup: (instanceId) => adminApiHelpers.closeSurveyInstancesByGroup(instanceId, instance, accounts),
      deleteSurveyInstance: (instanceId) => adminApiHelpers.deleteSurveyInstance(instanceId, instance, accounts),
      getConfigurableMessage: (messageId) => adminApiHelpers.getConfigurableMessage(messageId, instance, accounts),
      updateConfigurableMessage: (messageId, messageData) => adminApiHelpers.updateConfigurableMessage(messageId, messageData, instance, accounts),
      deleteConfigurableMessage: (messageId) => adminApiHelpers.deleteConfigurableMessage(messageId, instance, accounts),

    },

    // Roles/Permissions helpers
    roles: {
      getAll: () => rolesApiHelpers.getAll(instance, accounts),
      create: (roleData) => rolesApiHelpers.create(roleData, instance, accounts),
      update: (roleId, roleData) => rolesApiHelpers.update(roleId, roleData, instance, accounts),
      delete: (roleId) => rolesApiHelpers.delete(roleId, instance, accounts)
    },

     // Member-specific helpers (NEW!)
    member: {
      getSurveyInstance: (instanceId) => memberApiHelpers.getSurveyInstance(instanceId, instance, accounts),
      getSurveyStep: (instanceId, stepId) => memberApiHelpers.getSurveyStep(instanceId, stepId, instance, accounts),
      getOpenSurveys: () => memberApiHelpers.getOpenSurveys(instance, accounts),
      updateResponses: (instanceId, responses) => memberApiHelpers.updateResponses(instanceId, responses, instance, accounts),
      updateSurveyProgress: (instanceId, progress) => memberApiHelpers.updateSurveyProgress(instanceId, progress, instance, accounts),
    },

    // Batch operations
    batch: (requests) => apiHelpers.batch(requests, instance, accounts),

    // Security utilities
    security: {
      sanitizeInput: validation.sanitizeInput,
      isValidEmail: validation.isValidEmail,
      isValidEndpoint: validation.isValidEndpoint,
      getAuditLogs: securityLogger.getAuditLogs,
      clearAuditLogs: securityLogger.clearAuditLogs
    }
  };

  return {
    callAuthenticatedApi,
    isLoading,
    error,
    clearError: () => setError(null),
    secureHelpers
  };
}

/**
 * Security Helper Utilities for Admin Portal
 */

// Input validation helpers
export const validation = {
  /**
   * Sanitize user input for API calls
   * @param {string} input - Raw user input
   * @returns {string} - Sanitized input
   */
  sanitizeInput: (input) => {
    if (typeof input !== 'string') return '';
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
      .replace(/[<>]/g, '') // Remove angle brackets
      .trim();
  },

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} - Is valid email
   */
  isValidEmail: (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  /**
   * Validate API endpoint format
   * @param {string} endpoint - API endpoint
   * @returns {boolean} - Is valid endpoint
   */
  isValidEndpoint: (endpoint) => {
    return endpoint && typeof endpoint === 'string' && endpoint.startsWith('/api/');
  },

  /**
   * Validate JSON data before sending
   * @param {any} data - Data to validate
   * @returns {boolean} - Is valid for API
   */
  isValidApiData: (data) => {
    try {
      JSON.stringify(data);
      return true;
    } catch {
      return false;
    }
  }
};

// Rate limiting helper
export const rateLimiter = {
  requests: new Map(),
  
  /**
   * Check if request should be rate limited
   * @param {string} endpoint - API endpoint
   * @param {number} maxRequests - Max requests per window
   * @param {number} windowMs - Time window in ms
   * @returns {boolean} - Should limit request
   */
  shouldLimit: (endpoint, maxRequests = 10, windowMs = 60000) => {
    const now = Date.now();
    const requests = rateLimiter.requests.get(endpoint) || [];
    
    // Remove old requests outside window
    const validRequests = requests.filter(time => now - time < windowMs);
    
    if (validRequests.length >= maxRequests) {
      if (import.meta.env.DEV) {
        console.warn(`Rate limit exceeded for ${endpoint}. Max ${maxRequests} requests per ${windowMs/1000}s`);
      }
      return true;
    }
    
    // Add current request
    validRequests.push(now);
    rateLimiter.requests.set(endpoint, validRequests);
    return false;
  }
};

// Security audit logging
export const securityLogger = {
  /**
   * Log API call for security audit
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {boolean} success - Was call successful
   * @param {string} userId - User identifier
   */
  logApiCall: (endpoint, method, success, userId = 'unknown') => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      endpoint,
      method,
      success,
      userId,
      userAgent: navigator.userAgent,
      ip: 'client-side' // Would be logged server-side in real implementation
    };
    
    // In production, this would send to security monitoring service
    if (import.meta.env.DEV) {
      console.log('🔒 Security Audit Log:', logEntry);
      
      // Store in session for debugging (remove in production)
      const logs = JSON.parse(sessionStorage.getItem('security_logs') || '[]');
      logs.push(logEntry);
      if (logs.length > 100) logs.shift(); // Keep last 100 logs
      sessionStorage.setItem('security_logs', JSON.stringify(logs));
    }
  },

  /**
   * Get security audit logs
   * @returns {Array} - Array of security log entries
   */
  getAuditLogs: () => {
    return JSON.parse(sessionStorage.getItem('security_logs') || '[]');
  },

  /**
   * Clear security audit logs
   */
  clearAuditLogs: () => {
    sessionStorage.removeItem('security_logs');
  }
};

// Enhanced API helpers with security features
export const apiHelpers = {
  /**
   * Secure GET request
   */
  get: async (endpoint, msalInstance, accounts, options = {}) => {
    if (!validation.isValidEndpoint(endpoint)) {
      throw new Error('Invalid API endpoint format');
    }
    
    // Client-side rate limiting disabled - backend handles rate limiting
    // if (rateLimiter.shouldLimit(endpoint)) {
    //   throw new Error('Rate limit exceeded. Please try again later.');
    // }

    try {
      const result = await callApi(endpoint, { 
        method: 'GET',
        ...options 
      }, msalInstance, accounts);
      
      securityLogger.logApiCall(endpoint, 'GET', true, accounts[0]?.username);
      return result;
    } catch (error) {
      securityLogger.logApiCall(endpoint, 'GET', false, accounts[0]?.username);
      throw error;
    }
  },

   /**
     * Secure PATCH request
     */
    patch: async (endpoint, data, msalInstance, accounts, options = {}) => {
      if (!validation.isValidEndpoint(endpoint)) {
        throw new Error('Invalid API endpoint format');
      }

      // Client-side rate limiting disabled - backend handles rate limiting
      // if (rateLimiter.shouldLimit(endpoint, 5, 60000)) {
      //   throw new Error('Rate limit exceeded for PATCH. Please try again later.');
      // }

      try {
        const result = await callApi(endpoint, {
          method: 'PATCH',
          body: JSON.stringify(data),
          ...options
        }, msalInstance, accounts);

        securityLogger.logApiCall(endpoint, 'PATCH', true, accounts[0]?.username);
        return result;
      } catch (error) {
        securityLogger.logApiCall(endpoint, 'PATCH', false, accounts[0]?.username);
        throw error;
      }
    },
    
  /**
   * Secure POST request with input validation
   */
  post: async (endpoint, data, msalInstance, accounts, options = {}) => {
    if (!validation.isValidEndpoint(endpoint)) {
      throw new Error('Invalid API endpoint format');
    }

    if (!validation.isValidApiData(data)) {
      throw new Error('Invalid data format for API call');
    }
    
    // Client-side rate limiting disabled - backend handles rate limiting
    // if (rateLimiter.shouldLimit(endpoint, 5, 60000)) { // Stricter limits for POST
    //   throw new Error('Rate limit exceeded. Please try again later.');
    // }

    try {
      const result = await callApi(endpoint, {
        method: 'POST',
        body: JSON.stringify(data),
        ...options
      }, msalInstance, accounts);
      
      securityLogger.logApiCall(endpoint, 'POST', true, accounts[0]?.username);
      return result;
    } catch (error) {
      securityLogger.logApiCall(endpoint, 'POST', false, accounts[0]?.username);
      throw error;
    }
  },
    
  /**
   * Secure PUT request with input validation
   */
  put: async (endpoint, data, msalInstance, accounts, options = {}) => {
    if (!validation.isValidEndpoint(endpoint)) {
      throw new Error('Invalid API endpoint format');
    }

    if (!validation.isValidApiData(data)) {
      throw new Error('Invalid data format for API call');
    }
    
    // Client-side rate limiting disabled - backend handles rate limiting
    // if (rateLimiter.shouldLimit(endpoint, 5, 60000)) { // Stricter limits for PUT
    //   throw new Error('Rate limit exceeded. Please try again later.');
    // }

    try {
      const result = await callApi(endpoint, {
        method: 'PUT',
        body: JSON.stringify(data),
        ...options
      }, msalInstance, accounts);
      
      securityLogger.logApiCall(endpoint, 'PUT', true, accounts[0]?.username);
      return result;
    } catch (error) {
      securityLogger.logApiCall(endpoint, 'PUT', false, accounts[0]?.username);
      throw error;
    }
  },

  /**
   * Secure DELETE request with confirmation
   */
  delete: async (endpoint, msalInstance, accounts, options = {}) => {
    if (!validation.isValidEndpoint(endpoint)) {
      throw new Error('Invalid API endpoint format');
    }
    
    // Client-side rate limiting disabled - backend handles rate limiting
    // if (rateLimiter.shouldLimit(endpoint, 3, 60000)) { // Very strict limits for DELETE
    //   throw new Error('Rate limit exceeded. Please try again later.');
    // }

    // Require explicit confirmation for delete operations

    try {
      const result = await callApi(endpoint, { 
        method: 'DELETE',
        ...options 
      }, msalInstance, accounts);
      
      securityLogger.logApiCall(endpoint, 'DELETE', true, accounts[0]?.username);
      return result;
    } catch (error) {
      securityLogger.logApiCall(endpoint, 'DELETE', false, accounts[0]?.username);
      throw error;
    }
  },

  /**
   * Bulk operations with enhanced security
   */
  batch: async (requests, msalInstance, accounts) => {
    if (!Array.isArray(requests) || requests.length === 0) {
      throw new Error('Batch requests must be a non-empty array');
    }

    if (requests.length > 10) {
      throw new Error('Batch size limited to 10 requests for security');
    }

    const results = [];
    for (const request of requests) {
      const { endpoint, method, data, options } = request;
      
      try {
        let result;
        switch (method.toUpperCase()) {
          case 'GET':
            result = await apiHelpers.get(endpoint, msalInstance, accounts, options);
            break;
          case 'POST':
            result = await apiHelpers.post(endpoint, data, msalInstance, accounts, options);
            break;
          case 'PUT':
            result = await apiHelpers.put(endpoint, data, msalInstance, accounts, options);
            break;
          case 'DELETE':
            result = await apiHelpers.delete(endpoint, msalInstance, accounts, { ...options, confirmed: true });
            break;
          default:
            throw new Error(`Unsupported method: ${method}`);
        }
        
        results.push({ success: true, data: result });
      } catch (error) {
        results.push({ success: false, error: error.message });
      }
    }
    
    return results;
  }
};

//***************. Admin-specific secure API helpers
export const adminApiHelpers = {
  /**
   * Get all users (admin only)
   */
  getAllUsers: (msalInstance, accounts) => 
    apiHelpers.get('/api/admin/users', msalInstance, accounts),

  /**
   * Create new user (admin only)
   */
  createUser: (userData, msalInstance, accounts) => {
    // Additional validation for user creation
    if (!userData.email || !validation.isValidEmail(userData.email)) {
      throw new Error('Valid email required for user creation');
    }
    
    const sanitizedData = {
      ...userData,
      email: validation.sanitizeInput(userData.email),
      name: validation.sanitizeInput(userData.name || ''),
      role: validation.sanitizeInput(userData.role || 'user')
    };
    
    return apiHelpers.post('/api/admin/users', sanitizedData, msalInstance, accounts);
  },

  /**
   * Update user (admin only)
   */
  updateUser: (userId, userData, msalInstance, accounts) => {
    if (!userId || typeof userId !== 'string') {
      throw new Error('Valid user ID required');
    }
    
    const sanitizedData = {
      ...userData,
      email: userData.email ? validation.sanitizeInput(userData.email) : undefined,
      name: userData.name ? validation.sanitizeInput(userData.name) : undefined,
      role: userData.role ? validation.sanitizeInput(userData.role) : undefined
    };
    
    return apiHelpers.put(`/api/admin/users/${userId}`, sanitizedData, msalInstance, accounts);
  },

  /**
   * Delete user (admin only) 
   */
  deleteUser: (userId, msalInstance, accounts) => {
    if (!userId || typeof userId !== 'string') {
      throw new Error('Valid user ID required');
    }
    
    return apiHelpers.delete(`/api/admin/users/${userId}`, msalInstance, accounts, { confirmed: true });
  },

  /**
   * Get system settings (admin only)
   */
  getSystemSettings: (msalInstance, accounts) =>
    apiHelpers.get('/api/admin/settings', msalInstance, accounts),

  /**
   * Update system settings (admin only)
   */
  updateSystemSettings: (settings, msalInstance, accounts) =>
    apiHelpers.put('/api/admin/settings', settings, msalInstance, accounts),

  /**
   * Get audit logs (admin only)
   */
  getAuditLogs: (msalInstance, accounts, filters = {}) => {
    if (!import.meta.env.DEV) {
      throw new Error('Audit logs are only available in development mode');
    }
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = `/api/admin/audit-logs${queryParams ? `?${queryParams}` : ''}`;
    return apiHelpers.get(endpoint, msalInstance, accounts);
  },
    
  /**
   * Get all surveys for admin monitoring (admin only)
   */
  getAllSurveys: (msalInstance, accounts) =>
    apiHelpers.get('/api/Surveys?filters=All', msalInstance, accounts),

  /**
   * Get survey instance groups for monitoring (admin only)
   * Returns surveys grouped with their instance counts and progress stats
   */
  getSurveyInstanceGroups: (tags, includeClosed, msalInstance, accounts) => {
    const params = new URLSearchParams();
    (tags || []).forEach(t => params.append('tags', t));
    if (includeClosed) params.append('ignoreInstanceStatus', 'true');
    const qs = params.toString();
    return apiHelpers.get(`/api/SurveyInstances/Groups${qs ? `?${qs}` : ''}`, msalInstance, accounts);
  },

  /**
   * Get survey instances (hospice assignments) for a specific group (admin only)
   * @param groupKey - Key from getSurveyInstanceGroups (format: base64)
   */
  getSurveyInstancesByGroup: (groupKey, msalInstance, accounts) => {
    if (!groupKey || typeof groupKey !== 'string') {
      throw new Error('Valid group key required');
    }
    return apiHelpers.get(`/api/SurveyInstances/Groups/${encodeURIComponent(groupKey)}`, msalInstance, accounts);
  },

  /**
   * Toggle a survey instance open/closed state
   */
  toggleSurveyInstance: (instanceId, isOpen, msalInstance, accounts) => {
    if (!instanceId || typeof instanceId !== 'number') {
      throw new Error('Valid instance ID required');
    }
    return apiHelpers.patch(`/api/SurveyInstances/${instanceId}`, { isOpen }, msalInstance, accounts);
  },

/**
 * Close all open survey instances in a group  (admin only)
 * @param groupKey - Key from getSurveyInstanceGroups (format: base64)
 */
  closeSurveyInstancesByGroup: (groupKey, msalInstance, accounts) => {
    if (!groupKey || typeof groupKey !== 'string') {
      throw new Error('Valid group key required');
    }
    return apiHelpers.patch(`/api/SurveyInstances/Groups/${encodeURIComponent(groupKey)}`, { isOpen: false }, msalInstance, accounts);
  },

  /**
   * Delete a single survey instance permanently (admin only)
   */
  deleteSurveyInstance: (instanceId, msalInstance, accounts) => {
    if (!instanceId || typeof instanceId !== 'number') {
      throw new Error('Valid instance ID required');
    }
    return apiHelpers.delete(`/api/SurveyInstances/${instanceId}`, msalInstance, accounts, { confirmed: true });
  },

  getConfigurableMessage: (messageId, msalInstance, accounts) => {
    if (!messageId || typeof messageId !== 'string') {
      throw new Error('Valid message ID required');
    }
    return apiHelpers.get(`/api/admin/configurable-messages/${encodeURIComponent(messageId)}`, msalInstance, accounts);
  },

  updateConfigurableMessage: (messageId, messageData, msalInstance, accounts) => {
    if (!messageId || typeof messageId !== 'string') {
      throw new Error('Valid message ID required');
    }
    return apiHelpers.put(`/api/admin/configurable-messages/${encodeURIComponent(messageId)}`, messageData, msalInstance, accounts);
  },

  deleteConfigurableMessage: (messageId, msalInstance, accounts) => {
    if (!messageId || typeof messageId !== 'string') {
      throw new Error('Valid message ID required');
    }
    return apiHelpers.delete(`/api/admin/configurable-messages/${encodeURIComponent(messageId)}`, msalInstance, accounts);
  },
  /**
   * Manually close a survey (admin only)
   * Marks survey instances in progress as complete
   */
  closeSurvey: (surveyId, msalInstance, accounts) => {
    if (!surveyId || typeof surveyId !== 'number') {
      throw new Error('Valid survey ID required');
    }
    return apiHelpers.post(`/api/admin/surveys/${surveyId}/close`, {}, msalInstance, accounts);
  },

  /**
   * Delete a survey (admin only)
   */
  deleteSurvey: (surveyId, msalInstance, accounts) => {
    if (!surveyId || typeof surveyId !== 'number') {
      throw new Error('Valid survey ID required');
    }
    return apiHelpers.delete(`/api/Surveys/${surveyId}`, msalInstance, accounts, { confirmed: true });
  }
};
// get configurable message by ID for message setting admin UIs (admin only)

// *************. Roles/Permissions secure API helpers. ***************

export const rolesApiHelpers = {
  /**
   * Get all roles
   */
  getAll: (msalInstance, accounts) =>
    apiHelpers.get('/api/roles', msalInstance, accounts),

  /**
   * Create new role
   * @param {Object} roleData - { id: string, name: string, description: string }
   */
  create: (roleData, msalInstance, accounts) => {
    if (!roleData.id || !roleData.name || !roleData.description) {
      throw new Error('Role ID, name, and description are required');
    }
    return apiHelpers.post('/api/roles', roleData, msalInstance, accounts);
  },

  /**
   * Update existing role
   * @param {string} roleId - Role ID to update
   * @param {Object} roleData - { name: string, description: string }
   */
  update: (roleId, roleData, msalInstance, accounts) => {
    if (!roleId) {
      throw new Error('Role ID is required');
    }
    return apiHelpers.put(`/api/roles/${encodeURIComponent(roleId)}`, roleData, msalInstance, accounts);
  },

  /**
   * Delete role
   * @param {string} roleId - Role ID to delete
   */
  delete: (roleId, msalInstance, accounts) => {
    if (!roleId) {
      throw new Error('Role ID is required');
    }
    return apiHelpers.delete(`/api/roles/${encodeURIComponent(roleId)}`, msalInstance, accounts, { confirmed: true });
  }
};

// *************. Member-specific secure API helpers. ***************

export const memberApiHelpers = {
  /**
   * Get survey instance with all steps and questions
   */
  getSurveyInstance: (instanceId, msalInstance, accounts) => 
    apiHelpers.get(`/api/surveys/${instanceId}`, msalInstance, accounts),

  /**
   * Get specific step with questions
   */
  getSurveyStep: (instanceId, stepId, msalInstance, accounts) =>
    apiHelpers.get(`/api/surveys/${instanceId}/steps/${stepId}`, msalInstance, accounts),

  /**
   * Get all open surveys for current member
   */
  getOpenSurveys: (msalInstance, accounts) =>
    apiHelpers.get('/api/surveys?filters=OpenOnly', msalInstance, accounts),

  /**
   * Update survey responses (auto-save)
   */
  updateResponses: (instanceId, responsesArray, msalInstance, accounts) => {
    if (!Array.isArray(responsesArray)) {
      throw new Error('Responses must be an array');
    }

    // Validate each response has required fields
    const validatedResponses = responsesArray.map(r => ({
      id: r.id || null,
      subQuestionId: parseInt(r.subQuestionId || r.SubQuestionId),
      value: r.value ? validation.sanitizeInput(r.value) : null,
      notAnsweredReason: r.notAnsweredReason ? validation.sanitizeInput(r.notAnsweredReason) : null
    }));

    return apiHelpers.patch(`/api/surveys/${instanceId}/responses`, validatedResponses, msalInstance, accounts);
  },

  /**
   * Update survey progress status
   */
  updateSurveyProgress: (instanceId, progress, msalInstance, accounts) => {
    const validStatuses = ['NotStarted', 'InProgress', 'Completed', 'Submitted'];
    if (!validStatuses.includes(progress)) {
      throw new Error(`Invalid progress status. Must be one of: ${validStatuses.join(', ')}`);
    }

    return apiHelpers.patch(`/api/surveys/${instanceId}`, { progress }, msalInstance, accounts);
  }
};

const downloadBlob = async (endpoint) => {
  try {
    const response = await callApi(
      endpoint,
      { method: 'GET', responseType: 'blob' },
      msalInstance,
      accounts
    );

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    return await response.blob();
  } catch (error) {
    throw new Error(`Download failed: ${error.message}`);
  }
};

// usage;
// const data = await secureHelpers.roles.getAll();
// await secureHelpers.roles.create(roleData);
// await secureHelpers.roles.delete(roleId);

// OR

// const data = await secureHelpers.get('/api/roles');
// await secureHelpers.post('/api/roles', roleData);
// await secureHelpers.delete(`/api/roles/${roleId}`);
